import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import classNames from 'classnames';

import Button from '../../atoms/Button';
import Checkbox from '../../atoms/Checkbox';
import Radio from '../../atoms/Radio';
import Icon from '../../atoms/Icon';
import {
  FlatHierarchicalSelectOption,
  HierarchicalSelectOption,
  ValueMap,
} from './types';
import { findItem } from './utils';

import styles from './HierarchicalSelect.module.scss';

export interface Props {
  option: HierarchicalSelectOption;
  maxDepth: number;
  expanded: string[][];
  selection: ValueMap;
  flatOptions: FlatHierarchicalSelectOption[];
  disabled: boolean;
  multiple: boolean;
  /** Plays a fade-out transition while the section is being removed */
  leaving?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, depth: number) => void;
  onExpand: (id: string, depth: number) => void;
  onCollapse: (id: string, depth: number) => void;
}

export const SelectSection: React.FunctionComponent<Props> = ({
  option,
  maxDepth,
  expanded,
  selection,
  flatOptions,
  disabled,
  multiple,
  leaving = false,
  onChange,
  onExpand,
  onCollapse,
}) => {
  const Component = multiple ? Checkbox : Radio;

  /**
   * Renders itens recursively
   */
  const renderItemDepth = React.useCallback(
    (opt: HierarchicalSelectOption, currentDepth = 0) => {
      const isExpanded = expanded[currentDepth]?.includes(opt.id) || false;

      const path = flatOptions.find((flat) => flat.id === opt.id)?.path || [];
      const item = findItem(selection, path);
      return (
        <>
          <div className={styles['collapsible-section-header']}>
            <Component
              name={`depth-${currentDepth}`}
              value={opt.id}
              checked={item?.selected || false}
              indeterminate={
                !!item &&
                !!opt.children &&
                multiple &&
                Object.keys(item.selection).length !== opt.children.length
              }
              label={opt.label}
              description={opt.description}
              defaultTextWeight={
                currentDepth < maxDepth ? 'semiBold' : undefined
              }
              onChange={(e) => onChange(e, currentDepth)}
              disabled={disabled}
            />

            {currentDepth < maxDepth &&
              opt.children &&
              opt.children.length > 0 && (
                <Button
                  variant="link"
                  onClick={() => {
                    if (isExpanded) onCollapse(opt.id, currentDepth);
                    else onExpand(opt.id, currentDepth);
                  }}
                >
                  <Icon
                    color="primary"
                    icon={isExpanded ? faChevronUp : faChevronDown}
                  />
                </Button>
              )}
          </div>

          {currentDepth < maxDepth &&
            isExpanded &&
            opt.children &&
            opt.children.map((child) => (
              <div
                key={child.id}
                className={classNames(
                  styles['collapsible-section-depth'],
                  `depth-${currentDepth + 1}`,
                )}
              >
                {renderItemDepth(child, currentDepth + 1)}
              </div>
            ))}
        </>
      );
    },
    [
      Component,
      disabled,
      expanded,
      flatOptions,
      maxDepth,
      multiple,
      onChange,
      onCollapse,
      onExpand,
      selection,
    ],
  );
  return (
    <div className={classNames(styles['collapsible-section'], { leaving })}>
      <div
        className={classNames(styles['collapsible-section-depth'], `depth-0`)}
      >
        {renderItemDepth(option)}
      </div>
    </div>
  );
};

export default SelectSection;
